/**
 * The pods the Odoo chart adds to the platform's contract (genericity plan,
 * step 6, 2026-10-24): its shell and its init Job, the roles the Odoo
 * module gives the core's pod names (`AppTypeModule.pods`,
 * `projectEnvironments/podNames.ts` of the core). Formerly the core's own
 * rules, same patterns, same order.
 *
 * - the shell Deployment (chart 0.9.8 and later), `<fullname>-shell`
 *   (`odoo.shellName`), at 0 replicas until someone starts it: pods
 *   `<fullname>-shell-<replicaset hash>-<pod id>`. Three segments after the
 *   release, which the main Deployment's pattern (two) would not take anyway:
 *   without this rule they would read as `other`, never as Odoo. It serves
 *   no traffic and stops on its own: never a default pod, no version badge,
 *   no upload;
 * - the init Job, `<fullname>-init-<5>`: tested before the main Deployment,
 *   whose pattern it would match too (`init` reads as a pod-template hash).
 *   Never a default pod, but the Logs tab's last resort.
 */

import { useMemo } from 'react';
import { useTranslationRef } from '@backstage/frontend-plugin-api';
import { embeddedOdooDescriptor } from '@internal/plugin-waadoo-config-common';
import {
  friendlyPodName,
  podNamingOf,
  type PodInfo,
  type PodRoleRule,
} from '@internal/plugin-waadoo-core';
import { odooTranslationRef } from './translation';

/** The role of the shell's pods. */
export const SHELL_ROLE = 'shell';
/** The role of the init Job's pods. */
export const INIT_ROLE = 'init';

/** The Odoo chart's roles, with the labels to show. */
export function odooPodRoles(labels: {
  shell: string;
  init: string;
}): PodRoleRule[] {
  return [
    {
      role: INIT_ROLE,
      pattern: /^init-([a-z0-9]{5})$/,
      label: labels.init,
      defaultPick: 'lastResort',
      auxiliary: false,
    },
    {
      role: SHELL_ROLE,
      pattern: /^shell-[a-z0-9]{1,10}-([a-z0-9]{5})$/,
      label: labels.shell,
      defaultPick: 'never',
      auxiliary: true,
    },
  ];
}

/** The module's roles, labels translated (`AppTypeModule.pods.useRoles`). */
export function useOdooPodRoles(): readonly PodRoleRule[] {
  const { t } = useTranslationRef(odooTranslationRef);
  const shell = t('podName.shell');
  const init = t('podName.init');
  return useMemo(() => odooPodRoles({ shell, init }), [shell, init]);
}

/**
 * The naming of an Odoo environment's pods for the roles alone: the chart's
 * `fullnameSuffix` (`-odoo`) is the embedded descriptor's, the same as the
 * catalog's (platform-catalog `apps/odoo/apptype.yaml`); the labels do not
 * matter to a role.
 */
const ROLE_NAMING = podNamingOf(
  embeddedOdooDescriptor(),
  '',
  odooPodRoles({ shell: '', init: '' }),
);

/** The Odoo shell's pod (header). */
export function isShellPod(pod: Pick<PodInfo, 'name' | 'namespace'>): boolean {
  return (
    friendlyPodName(pod.name, { namespace: pod.namespace, naming: ROLE_NAMING })
      .role === SHELL_ROLE
  );
}

/** The chart's init Job pod (header). */
export function isInitJobPod(
  pod: Pick<PodInfo, 'name' | 'namespace'>,
): boolean {
  return (
    friendlyPodName(pod.name, { namespace: pod.namespace, naming: ROLE_NAMING })
      .role === INIT_ROLE
  );
}
